/**
 * Hook React para integração com sistema de criptografia PQC
 * 
 * Fornece funcionalidades para:
 * - Geração de chaves Kyber e Dilithium
 * - Criptografia e descriptografia de dados
 * - Assinatura e verificação de documentos
 * - Gerenciamento de chaves do usuário
 */

import { useState, useEffect, useCallback } from 'react'
import { toast } from 'sonner'
import pqcCrypto, { KyberKeyPair, DilithiumKeyPair, EncryptedData, SignedData } from '../lib/pqc-crypto'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import { useDataSync } from './useDataSync'

interface PQCKeyInfo {
  id: string
  algorithm: string
  createdAt: string
  expiresAt?: string
  isActive: boolean
  usage: 'primary' | 'backup' | 'archived'
}

interface PQCSecurityLog {
  id: string
  action: 'key_generated' | 'key_used' | 'key_expired' | 'encryption' | 'decryption' | 'signing' | 'verification' | 'key_rotation' | 'key_import' | 'key_validation'
  timestamp: string
  details: string
  success: boolean
  type?: string
  userId?: string
}

interface PQCState {
  isInitialized: boolean
  kyberKeys: KyberKeyPair | null
  dilithiumKeys: DilithiumKeyPair | null
  keyInfo: {
    kyber: PQCKeyInfo | null
    dilithium: PQCKeyInfo | null
  }
  securityLogs: PQCSecurityLog[]
  securityStatus: {
    level: 'high' | 'medium' | 'low' | 'critical'
    message: string
    lastCheck: string
  }
  isGeneratingKeys: boolean
  isEncrypting: boolean
  isDecrypting: boolean
  isSigning: boolean
  isVerifying: boolean
  isBackingUp: boolean
  isRestoring: boolean
}

interface PQCOperations {
  generateKeys: (kyberAlg?: 'Kyber-512' | 'Kyber-768' | 'Kyber-1024', dilithiumAlg?: 'Dilithium2' | 'Dilithium3' | 'Dilithium5') => Promise<void>
  encryptData: (data: string, recipientPublicKey: string) => Promise<EncryptedData | null>
  decryptData: (encryptedData: EncryptedData) => Promise<string | null>
  signData: (data: string) => Promise<SignedData | null>
  verifySignature: (signedData: SignedData) => Promise<boolean>
  encryptAndSign: (data: string, recipientPublicKey: string) => Promise<EncryptedData | null>
  decryptAndVerify: (encryptedData: EncryptedData) => Promise<{ data: string; isSignatureValid: boolean } | null>
  saveKeysToDatabase: () => Promise<void>
  loadKeysFromDatabase: () => Promise<void>
  removeKeys: () => Promise<void>
  getPublicKeys: () => { kyber: string | null; dilithium: string | null }
  hasValidKeys: () => boolean
  // Novas funcionalidades avançadas
  backupKeys: () => Promise<string | null>
  restoreKeys: (backupData: string) => Promise<boolean>
  rotateKeys: () => Promise<void>
  checkKeyExpiration: () => { kyber: boolean; dilithium: boolean }
  getSecurityStatus: () => { level: string; message: string; recommendations: string[] }
  addSecurityLog: (action: string, details: string, success: boolean) => void
  clearSecurityLogs: () => void
  exportPublicKeys: () => string
  importPublicKey: (publicKeyData: string, type: 'kyber' | 'dilithium') => boolean
  validateKeyIntegrity: () => Promise<boolean>
  getKeyMetrics: () => { 
    strength: number; 
    algorithm: string; 
    age: number;
    totalKeys: number;
    operationsToday: number;
    lastRotation: string | null;
    algorithms: { kyber: string; dilithium: string }
  }
}

export function usePQC(): PQCState & PQCOperations {
  const { user } = useAuth()
  const { registerRevalidation } = useDataSync()
  const [state, setState] = useState<PQCState>({
    isInitialized: false,
    kyberKeys: null,
    dilithiumKeys: null,
    keyInfo: {
      kyber: null,
      dilithium: null
    },
    securityLogs: [],
    securityStatus: {
      level: 'critical',
      message: 'Chaves PQC não encontradas',
      lastCheck: new Date().toISOString()
    },
    isGeneratingKeys: false,
    isEncrypting: false,
    isDecrypting: false,
    isSigning: false,
    isVerifying: false,
    isBackingUp: false,
    isRestoring: false
  })

  // Inicializar PQC quando usuário estiver logado
  useEffect(() => {
    if (user) {
      initializePQC()
    } else {
      // Limpar estado quando usuário sair
      setState(prev => ({
        ...prev,
        isInitialized: false,
        kyberKeys: null,
        dilithiumKeys: null
      }))
    }
  }, [user])

  // Função de log de segurança (deve vir antes das outras funções que a utilizam)
  const addSecurityLog = useCallback((action: string, details: string, success: boolean) => {
    const log: PQCSecurityLog = {
      id: `log_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      action: action as any,
      timestamp: new Date().toISOString(),
      details,
      success
    }
    
    setState(prev => ({
      ...prev,
      securityLogs: [log, ...prev.securityLogs].slice(0, 100) // Manter apenas os últimos 100 logs
    }))
  }, [])

  const loadKeysFromDatabase = useCallback(async () => {
    console.log('🔍 Iniciando carregamento das chaves PQC do banco...')
    console.log('User ID:', user?.id)
    
    if (!user) {
      console.log('❌ Usuário não encontrado, cancelando carregamento')
      return
    }

    try {
      console.log('📡 Buscando chaves no banco de dados...')
      const { data: keysData, error } = await supabase
        .from('pqc_keys')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_active', true)

      if (error) {
        console.error('❌ Erro ao buscar chaves no banco:', error)
        console.error('Error details:', {
          message: error.message,
          code: error.code,
          details: error.details,
          hint: error.hint
        })
        return
      }

      console.log('📊 Resultado da busca:', {
        found: !!keysData,
        count: keysData?.length || 0,
        keys: keysData?.map(k => ({ id: k.id, type: k.key_type, algorithm: k.algorithm })) || []
      })

      if (!keysData || keysData.length === 0) {
        console.log('⚠️ Nenhuma chave PQC encontrada no banco para o usuário')
        return
      }

      // Separar chaves por tipo
      const kyberKeyData = keysData.find(key => key.key_type === 'kyber')
      const dilithiumKeyData = keysData.find(key => key.key_type === 'dilithium')

      console.log('🔑 Chaves encontradas:', {
        hasKyber: !!kyberKeyData,
        hasDilithium: !!dilithiumKeyData,
        kyberAlgorithm: kyberKeyData?.algorithm,
        dilithiumAlgorithm: dilithiumKeyData?.algorithm
      })

      if (!kyberKeyData || !dilithiumKeyData) {
        console.warn('⚠️ Chaves incompletas encontradas no banco:', {
          missingKyber: !kyberKeyData,
          missingDilithium: !dilithiumKeyData
        })
        return
      }

      const kyberKeys: KyberKeyPair = {
        publicKey: kyberKeyData.public_key,
        privateKey: kyberKeyData.private_key_encrypted, // Em produção, deveria ser descriptografada
        algorithm: kyberKeyData.algorithm,
        createdAt: kyberKeyData.created_at
      }

      const dilithiumKeys: DilithiumKeyPair = {
        publicKey: dilithiumKeyData.public_key,
        privateKey: dilithiumKeyData.private_key_encrypted, // Em produção, deveria ser descriptografada
        algorithm: dilithiumKeyData.algorithm,
        createdAt: dilithiumKeyData.created_at
      }

      console.log('🔧 Carregando chaves no sistema PQC...')
      // Carregar chaves no sistema PQC
      pqcCrypto.setUserKeys(user.id, kyberKeys, dilithiumKeys)

      console.log('💾 Atualizando estado local...')
      setState(prev => {
        const newState = {
          ...prev,
          kyberKeys,
          dilithiumKeys,
          isInitialized: true
        }
        
        // Calcular status de segurança baseado nas chaves carregadas
        const securityStatus = getSecurityStatusForKeys(kyberKeys, dilithiumKeys)
        
        return {
          ...newState,
          securityStatus: {
            ...securityStatus,
            lastCheck: new Date().toISOString()
          }
        }
      })

      console.log('✅ Chaves PQC carregadas do banco com sucesso!')
      console.log('📊 Estado atualizado:', {
        hasKyberKeys: !!kyberKeys,
        hasDilithiumKeys: !!dilithiumKeys,
        isInitialized: true
      })
      
      addSecurityLog('key_loaded', 'Chaves PQC carregadas do banco de dados', true)
      toast.success('Chaves PQC carregadas do banco de dados')
       
    } catch (error: any) {
      console.error('💥 Erro crítico ao carregar chaves do banco:', error)
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        details: error.details,
        hint: error.hint
      })
      addSecurityLog('key_loaded', `Falha ao carregar chaves: ${error.message}`, false)
    }
  }, [user, addSecurityLog])

  const initializePQC = useCallback(async () => {
    console.log('🚀 INICIALIZANDO SISTEMA PQC - INÍCIO')
    console.log('👤 Usuário atual:', user?.id)
    
    try {
      if (!user) {
        console.log('❌ Usuário não encontrado, cancelando inicialização')
        return
      }

      console.log('🔍 Verificando chaves no cache local...')
      // Verificar se já tem chaves no sistema
      const userKeys = pqcCrypto.getUserKeys(user.id)
      
      console.log('📊 Resultado do cache:', {
        hasKyberInCache: !!userKeys.kyber,
        hasDilithiumInCache: !!userKeys.dilithium
      })
      
      if (userKeys.kyber && userKeys.dilithium) {
        console.log('✅ Chaves encontradas no cache local - atualizando estado')
        setState(prev => {
          const securityStatus = getSecurityStatusForKeys(userKeys.kyber, userKeys.dilithium)
          return {
            ...prev,
            isInitialized: true,
            kyberKeys: userKeys.kyber,
            dilithiumKeys: userKeys.dilithium,
            securityStatus: {
              ...securityStatus,
              lastCheck: new Date().toISOString()
            }
          }
        })
        console.log('📊 Estado atualizado com chaves do cache')
      } else {
        console.log('⚠️ Chaves não encontradas no cache, tentando carregar do banco...')
        // Tentar carregar do banco de dados
        await loadKeysFromDatabase()
        
        // Verificar novamente após carregar do banco
        const updatedKeys = pqcCrypto.getUserKeys(user.id)
        console.log('📊 Após carregar do banco:', {
          hasKyberAfterDB: !!updatedKeys.kyber,
          hasDilithiumAfterDB: !!updatedKeys.dilithium
        })
      }

      console.log('🎉 SISTEMA PQC INICIALIZADO - FINALIZANDO')
      setState(prev => {
        const securityStatus = getSecurityStatusForKeys(prev.kyberKeys, prev.dilithiumKeys)
        return { 
          ...prev, 
          isInitialized: true,
          securityStatus: {
            ...securityStatus,
            lastCheck: new Date().toISOString()
          }
        }
      })
      
      // Log final do estado
      console.log('📊 Estado final da inicialização:', {
        isInitialized: true,
        timestamp: new Date().toISOString()
      })
    } catch (error) {
      console.error('💥 Erro ao inicializar PQC:', error)
      toast.error('Erro ao inicializar sistema de criptografia')
    }
  }, [user, loadKeysFromDatabase])

  const generateKeys = useCallback(async (
    kyberAlg: 'Kyber-512' | 'Kyber-768' | 'Kyber-1024' = 'Kyber-768',
    dilithiumAlg: 'Dilithium2' | 'Dilithium3' | 'Dilithium5' = 'Dilithium3'
  ) => {
    if (!user) {
      console.error('❌ Usuário não autenticado para geração de chaves')
      toast.error('Usuário não autenticado')
      return
    }

    console.log('🚀 INICIANDO GERAÇÃO DE CHAVES PQC')
    console.log('👤 Usuário:', user.id)
    console.log('🔧 Algoritmos:', { kyber: kyberAlg, dilithium: dilithiumAlg })
    
    setState(prev => ({ ...prev, isGeneratingKeys: true }))

    try {
      console.log('🔑 Iniciando geração de chaves PQC...')
      
      // Gerar chaves Kyber
      console.log('🔧 Gerando chaves Kyber...')
      const kyberKeys = await pqcCrypto.generateKyberKeyPair(user.id, kyberAlg)
      console.log('✅ Chaves Kyber geradas:', {
        algorithm: kyberKeys.algorithm,
        publicKeyLength: kyberKeys.publicKey.length,
        privateKeyLength: kyberKeys.privateKey.length
      })
      
      // Gerar chaves Dilithium
      console.log('🔧 Gerando chaves Dilithium...')
      const dilithiumKeys = await pqcCrypto.generateDilithiumKeyPair(user.id, dilithiumAlg)
      console.log('✅ Chaves Dilithium geradas:', {
        algorithm: dilithiumKeys.algorithm,
        publicKeyLength: dilithiumKeys.publicKey.length,
        privateKeyLength: dilithiumKeys.privateKey.length
      })

      // Atualizar estado com as novas chaves
      console.log('💾 Atualizando estado local com as chaves geradas...')
      setState(prev => {
        const securityStatus = getSecurityStatusForKeys(kyberKeys, dilithiumKeys)
        return {
          ...prev,
          kyberKeys,
          dilithiumKeys,
          isInitialized: true, // IMPORTANTE: Marcar como inicializado após gerar chaves
          securityStatus: {
            ...securityStatus,
            lastCheck: new Date().toISOString()
          }
        }
      })
      
      console.log('📊 Estado atualizado:', {
        hasKyberKeys: !!kyberKeys,
        hasDilithiumKeys: !!dilithiumKeys,
        isInitialized: true
      })

      // Salvar no banco de dados passando as chaves diretamente
      console.log('💾 Salvando chaves no banco de dados...')
      await saveKeysToDatabase(kyberKeys, dilithiumKeys)
      
      console.log('🎉 CHAVES PQC GERADAS E SISTEMA INICIALIZADO COM SUCESSO!')
      toast.success('Chaves PQC geradas com sucesso!')
      
      // Log final do estado
      console.log('📊 Estado final após geração:', {
        isInitialized: true,
        hasValidKeys: !!(kyberKeys && dilithiumKeys)
      })
    } catch (error) {
      console.error('💥 Erro ao gerar chaves:', error)
      toast.error('Erro ao gerar chaves de criptografia')
    } finally {
      setState(prev => ({ ...prev, isGeneratingKeys: false }))
    }
  }, [user])

  const encryptData = useCallback(async (
    data: string,
    recipientPublicKey: string
  ): Promise<EncryptedData | null> => {
    setState(prev => ({ ...prev, isEncrypting: true }))

    try {
      const encrypted = await pqcCrypto.encryptData(data, recipientPublicKey)
      toast.success('Dados criptografados com sucesso')
      return encrypted
    } catch (error) {
      console.error('Erro ao criptografar:', error)
      toast.error('Erro ao criptografar dados')
      return null
    } finally {
      setState(prev => ({ ...prev, isEncrypting: false }))
    }
  }, [])

  const decryptData = useCallback(async (
    encryptedData: EncryptedData
  ): Promise<string | null> => {
    if (!state.kyberKeys) {
      toast.error('Chaves de descriptografia não encontradas')
      return null
    }

    setState(prev => ({ ...prev, isDecrypting: true }))

    try {
      const decrypted = await pqcCrypto.decryptData(
        encryptedData,
        state.kyberKeys.privateKey
      )
      toast.success('Dados descriptografados com sucesso')
      return decrypted
    } catch (error) {
      console.error('Erro ao descriptografar:', error)
      toast.error('Erro ao descriptografar dados')
      return null
    } finally {
      setState(prev => ({ ...prev, isDecrypting: false }))
    }
  }, [state.kyberKeys])

  const signData = useCallback(async (
    data: string
  ): Promise<SignedData | null> => {
    if (!state.dilithiumKeys) {
      toast.error('Chaves de assinatura não encontradas')
      return null
    }

    setState(prev => ({ ...prev, isSigning: true }))

    try {
      const signed = await pqcCrypto.signData(
        data,
        state.dilithiumKeys.privateKey,
        state.dilithiumKeys.algorithm
      )
      toast.success('Dados assinados com sucesso')
      return signed
    } catch (error) {
      console.error('Erro ao assinar:', error)
      toast.error('Erro ao assinar dados')
      return null
    } finally {
      setState(prev => ({ ...prev, isSigning: false }))
    }
  }, [state.dilithiumKeys])

  const verifySignature = useCallback(async (
    signedData: SignedData
  ): Promise<boolean> => {
    setState(prev => ({ ...prev, isVerifying: true }))

    try {
      const isValid = await pqcCrypto.verifySignature(signedData)
      
      if (isValid) {
        toast.success('Assinatura válida')
      } else {
        toast.error('Assinatura inválida')
      }
      
      return isValid
    } catch (error) {
      console.error('Erro ao verificar assinatura:', error)
      toast.error('Erro ao verificar assinatura')
      return false
    } finally {
      setState(prev => ({ ...prev, isVerifying: false }))
    }
  }, [])

  const encryptAndSign = useCallback(async (
    data: string,
    recipientPublicKey: string
  ): Promise<EncryptedData | null> => {
    if (!state.dilithiumKeys) {
      toast.error('Chaves de assinatura não encontradas')
      return null
    }

    setState(prev => ({ ...prev, isEncrypting: true, isSigning: true }))

    try {
      const encrypted = await pqcCrypto.encryptAndSign(
        data,
        recipientPublicKey,
        state.dilithiumKeys.privateKey
      )
      toast.success('Dados criptografados e assinados com sucesso')
      return encrypted
    } catch (error) {
      console.error('Erro ao criptografar e assinar:', error)
      toast.error('Erro ao criptografar e assinar dados')
      return null
    } finally {
      setState(prev => ({ ...prev, isEncrypting: false, isSigning: false }))
    }
  }, [state.dilithiumKeys])

  const decryptAndVerify = useCallback(async (
    encryptedData: EncryptedData
  ): Promise<{ data: string; isSignatureValid: boolean } | null> => {
    if (!state.kyberKeys) {
      toast.error('Chaves de descriptografia não encontradas')
      return null
    }

    setState(prev => ({ ...prev, isDecrypting: true, isVerifying: true }))

    try {
      const result = await pqcCrypto.decryptAndVerify(
        encryptedData,
        state.kyberKeys.privateKey
      )
      
      if (result.isSignatureValid) {
        toast.success('Dados descriptografados e assinatura verificada')
      } else {
        toast.warning('Dados descriptografados, mas assinatura inválida')
      }
      
      return result
    } catch (error) {
      console.error('Erro ao descriptografar e verificar:', error)
      toast.error('Erro ao descriptografar e verificar dados')
      return null
    } finally {
      setState(prev => ({ ...prev, isDecrypting: false, isVerifying: false }))
    }
  }, [state.kyberKeys])

  const saveKeysToDatabase = useCallback(async (
    kyberKeysParam?: KyberKeyPair,
    dilithiumKeysParam?: DilithiumKeyPair
  ) => {
    console.log('🔑 Iniciando salvamento das chaves PQC...')
    console.log('User:', user?.id)
    
    // Usar chaves passadas como parâmetro ou do estado
    const kyberKeys = kyberKeysParam || state.kyberKeys
    const dilithiumKeys = dilithiumKeysParam || state.dilithiumKeys
    
    console.log('Kyber Keys:', !!kyberKeys)
    console.log('Dilithium Keys:', !!dilithiumKeys)
    
    if (!user || !kyberKeys || !dilithiumKeys) {
      console.error('❌ Dados insuficientes para salvar chaves:', {
        hasUser: !!user,
        hasKyberKeys: !!kyberKeys,
        hasDilithiumKeys: !!dilithiumKeys,
        usingParams: !!(kyberKeysParam && dilithiumKeysParam)
      })
      toast.error('Dados insuficientes para salvar chaves')
      return
    }

    try {
      console.log('🔄 Desativando chaves antigas...')
      // Desativar chaves antigas primeiro
      const { error: deactivateError } = await supabase
        .from('pqc_keys')
        .update({ is_active: false })
        .eq('user_id', user.id)
        .eq('is_active', true)

      if (deactivateError) {
        console.warn('⚠️ Erro ao desativar chaves antigas (pode ser normal se não existirem):', deactivateError)
      } else {
        console.log('✅ Chaves antigas desativadas com sucesso')
      }

      // Salvar chave Kyber
      console.log('💾 Salvando chave Kyber...')
      const kyberData = {
        user_id: user.id,
        key_type: 'kyber',
        public_key: kyberKeys.publicKey,
        private_key_encrypted: kyberKeys.privateKey, // Em produção, deveria ser criptografada
        algorithm: kyberKeys.algorithm,
        key_size: kyberKeys.publicKey.length,
        key_purpose: 'encryption',
        is_active: true
      }
      
      console.log('Kyber data to insert:', {
        user_id: kyberData.user_id,
        key_type: kyberData.key_type,
        algorithm: kyberData.algorithm,
        key_size: kyberData.key_size,
        public_key_length: kyberData.public_key.length,
        private_key_length: kyberData.private_key_encrypted.length
      })

      const { data: kyberResult, error: kyberError } = await supabase
        .from('pqc_keys')
        .insert([kyberData])
        .select()

      if (kyberError) {
        console.error('❌ Erro ao salvar chave Kyber:', kyberError)
        toast.error(`Erro ao salvar chave Kyber: ${kyberError.message}`)
        throw kyberError
      }
      
      console.log('✅ Chave Kyber salva com sucesso:', kyberResult)

      // Salvar chave Dilithium
      console.log('💾 Salvando chave Dilithium...')
      const dilithiumData = {
        user_id: user.id,
        key_type: 'dilithium',
        public_key: dilithiumKeys.publicKey,
        private_key_encrypted: dilithiumKeys.privateKey, // Em produção, deveria ser criptografada
        algorithm: dilithiumKeys.algorithm,
        key_size: dilithiumKeys.publicKey.length,
        key_purpose: 'signing',
        is_active: true
      }
      
      console.log('Dilithium data to insert:', {
        user_id: dilithiumData.user_id,
        key_type: dilithiumData.key_type,
        algorithm: dilithiumData.algorithm,
        key_size: dilithiumData.key_size,
        public_key_length: dilithiumData.public_key.length,
        private_key_length: dilithiumData.private_key_encrypted.length
      })

      const { data: dilithiumResult, error: dilithiumError } = await supabase
        .from('pqc_keys')
        .insert([dilithiumData])
        .select()

      if (dilithiumError) {
        console.error('❌ Erro ao salvar chave Dilithium:', dilithiumError)
        toast.error(`Erro ao salvar chave Dilithium: ${dilithiumError.message}`)
        throw dilithiumError
      }
      
      console.log('✅ Chave Dilithium salva com sucesso:', dilithiumResult)

      console.log('🎉 Chaves PQC salvas no banco de dados com sucesso!')
      toast.success('Chaves PQC salvas no banco de dados com sucesso!')
      
      // Adicionar log de segurança
      addSecurityLog('key_generated', 'Chaves PQC salvas no banco de dados', true)
       
     } catch (error: any) {
       console.error('💥 Erro crítico ao salvar chaves no banco:', error)
       console.error('Error details:', {
         message: error.message,
         code: error.code,
         details: error.details,
         hint: error.hint
       })
       toast.error(`Erro ao salvar chaves: ${error.message || 'Erro desconhecido'}`)
       addSecurityLog('key_generated', `Falha ao salvar chaves: ${error.message}`, false)
     }
  }, [user, state.kyberKeys, state.dilithiumKeys, addSecurityLog])



  const removeKeys = useCallback(async () => {
    if (!user) return

    try {
      // Desativar chaves no banco
      const { error } = await supabase
        .from('pqc_keys')
        .update({ is_active: false })
        .eq('user_id', user.id)

      if (error) throw error

      // Remover do sistema PQC
      pqcCrypto.removeUserKeys(user.id)

      setState(prev => ({
        ...prev,
        kyberKeys: null,
        dilithiumKeys: null
      }))

      toast.success('Chaves removidas com sucesso')
    } catch (error) {
      console.error('Erro ao remover chaves:', error)
      toast.error('Erro ao remover chaves')
    }
  }, [user])

  const getPublicKeys = useCallback(() => {
    return {
      kyber: state.kyberKeys?.publicKey || null,
      dilithium: state.dilithiumKeys?.publicKey || null
    }
  }, [state.kyberKeys, state.dilithiumKeys])

  const hasValidKeys = useCallback(() => {
    const hasKeys = !!(state.kyberKeys && state.dilithiumKeys)
    console.log('🔍 VERIFICANDO CHAVES VÁLIDAS:', {
      hasKyberKeys: !!state.kyberKeys,
      hasDilithiumKeys: !!state.dilithiumKeys,
      isInitialized: state.isInitialized,
      result: hasKeys,
      kyberAlgorithm: state.kyberKeys?.algorithm || 'N/A',
      dilithiumAlgorithm: state.dilithiumKeys?.algorithm || 'N/A'
    })
    return hasKeys
  }, [state.kyberKeys, state.dilithiumKeys, state.isInitialized])

  // Novas funcionalidades avançadas

  const backupKeys = useCallback(async (): Promise<string | null> => {
    if (!state.kyberKeys || !state.dilithiumKeys || !user) {
      toast.error('Chaves não encontradas para backup')
      return null
    }

    setState(prev => ({ ...prev, isBackingUp: true }))

    try {
      const backupData = {
        userId: user.id,
        timestamp: new Date().toISOString(),
        kyberKeys: {
          publicKey: state.kyberKeys.publicKey,
          privateKey: state.kyberKeys.privateKey,
          algorithm: state.kyberKeys.algorithm
        },
        dilithiumKeys: {
          publicKey: state.dilithiumKeys.publicKey,
          privateKey: state.dilithiumKeys.privateKey,
          algorithm: state.dilithiumKeys.algorithm
        }
      }

      const backupString = btoa(JSON.stringify(backupData))
      addSecurityLog('key_backup', 'Backup de chaves realizado', true)
      toast.success('Backup das chaves criado com sucesso')
      return backupString
    } catch (error) {
      console.error('Erro ao criar backup:', error)
      addSecurityLog('key_backup', 'Falha no backup de chaves', false)
      toast.error('Erro ao criar backup das chaves')
      return null
    } finally {
      setState(prev => ({ ...prev, isBackingUp: false }))
    }
  }, [state.kyberKeys, state.dilithiumKeys, user, addSecurityLog])

  const restoreKeys = useCallback(async (backupData: string): Promise<boolean> => {
    if (!user) {
      toast.error('Usuário não autenticado')
      return false
    }

    setState(prev => ({ ...prev, isRestoring: true }))

    try {
      const decoded = JSON.parse(atob(backupData))
      
      if (decoded.userId !== user.id) {
        toast.error('Backup não pertence ao usuário atual')
        return false
      }

      const kyberKeys: KyberKeyPair = {
        publicKey: decoded.kyberKeys.publicKey,
        privateKey: decoded.kyberKeys.privateKey,
        algorithm: decoded.kyberKeys.algorithm,
        createdAt: decoded.timestamp
      }

      const dilithiumKeys: DilithiumKeyPair = {
        publicKey: decoded.dilithiumKeys.publicKey,
        privateKey: decoded.dilithiumKeys.privateKey,
        algorithm: decoded.dilithiumKeys.algorithm,
        createdAt: decoded.timestamp
      }

      setState(prev => ({
        ...prev,
        kyberKeys,
        dilithiumKeys
      }))

      await saveKeysToDatabase()
      addSecurityLog('key_restore', 'Chaves restauradas do backup', true)
      toast.success('Chaves restauradas com sucesso')
      return true
    } catch (error) {
      console.error('Erro ao restaurar backup:', error)
      addSecurityLog('key_restore', 'Falha na restauração de chaves', false)
      toast.error('Erro ao restaurar chaves do backup')
      return false
    } finally {
      setState(prev => ({ ...prev, isRestoring: false }))
    }
  }, [user, saveKeysToDatabase, addSecurityLog])

  const rotateKeys = useCallback(async () => {
    if (!user) {
      toast.error('Usuário não autenticado')
      return
    }

    try {
      // Criar backup das chaves atuais antes da rotação
      if (state.kyberKeys && state.dilithiumKeys) {
        await backupKeys()
      }

      // Gerar novas chaves
      await generateKeys()
      addSecurityLog('key_rotation', 'Rotação de chaves realizada', true)
      toast.success('Chaves rotacionadas com sucesso')
    } catch (error) {
      console.error('Erro na rotação de chaves:', error)
      addSecurityLog('key_rotation', 'Falha na rotação de chaves', false)
      toast.error('Erro na rotação de chaves')
    }
  }, [user, state.kyberKeys, state.dilithiumKeys, backupKeys, generateKeys, addSecurityLog])

  const checkKeyExpiration = useCallback(() => {
    const now = new Date()
    const kyberExpired = state.keyInfo.kyber?.expiresAt ? new Date(state.keyInfo.kyber.expiresAt) < now : false
    const dilithiumExpired = state.keyInfo.dilithium?.expiresAt ? new Date(state.keyInfo.dilithium.expiresAt) < now : false
    
    return { kyber: kyberExpired, dilithium: dilithiumExpired }
  }, [state.keyInfo])

  // Função auxiliar para calcular status de segurança baseado em chaves específicas
  const getSecurityStatusForKeys = useCallback((kyberKeys: KyberKeyPair | null, dilithiumKeys: DilithiumKeyPair | null) => {
    const recommendations: string[] = []
    let level: 'high' | 'medium' | 'low' | 'critical' = 'high'
    let message = 'Sistema PQC funcionando normalmente'

    if (!kyberKeys || !dilithiumKeys) {
      level = 'critical'
      message = 'Chaves PQC não encontradas'
      recommendations.push('Gere novas chaves PQC imediatamente')
    } else {
      // Verificar expiração das chaves fornecidas
      const now = new Date()
      const kyberExpired = state.keyInfo.kyber?.expiresAt ? new Date(state.keyInfo.kyber.expiresAt) < now : false
      const dilithiumExpired = state.keyInfo.dilithium?.expiresAt ? new Date(state.keyInfo.dilithium.expiresAt) < now : false
      
      if (kyberExpired || dilithiumExpired) {
        level = 'critical'
        message = 'Chaves PQC expiradas'
        recommendations.push('Rotacione as chaves imediatamente')
      }

      const keyAge = kyberKeys.createdAt ? 
        (Date.now() - new Date(kyberKeys.createdAt).getTime()) / (1000 * 60 * 60 * 24) : 0
      
      if (keyAge > 90) {
        level = level === 'critical' ? 'critical' : 'medium'
        message = 'Chaves PQC antigas'
        recommendations.push('Considere rotacionar as chaves')
      }

      if (keyAge > 30 && level === 'high') {
        level = 'medium'
        recommendations.push('Faça backup das chaves regularmente')
      }
    }

    return { level, message, recommendations }
  }, [state.keyInfo])

  const getSecurityStatus = useCallback(() => {
    return getSecurityStatusForKeys(state.kyberKeys, state.dilithiumKeys)
  }, [state.kyberKeys, state.dilithiumKeys, getSecurityStatusForKeys])

  const clearSecurityLogs = useCallback(() => {
    setState(prev => ({ ...prev, securityLogs: [] }))
    toast.success('Logs de segurança limpos')
  }, [])

  const exportPublicKeys = useCallback(() => {
    if (!state.kyberKeys || !state.dilithiumKeys) {
      toast.error('Chaves não encontradas')
      return ''
    }

    const exportData = {
      userId: user?.id,
      timestamp: new Date().toISOString(),
      kyberPublicKey: state.kyberKeys.publicKey,
      dilithiumPublicKey: state.dilithiumKeys.publicKey,
      algorithms: {
        kyber: state.kyberKeys.algorithm,
        dilithium: state.dilithiumKeys.algorithm
      }
    }

    return btoa(JSON.stringify(exportData))
  }, [state.kyberKeys, state.dilithiumKeys, user])

  const importPublicKey = useCallback((publicKeyData: string, type: 'kyber' | 'dilithium') => {
    try {
      const decoded = JSON.parse(atob(publicKeyData))
      
      if (type === 'kyber' && decoded.kyberPublicKey) {
        // Validar e armazenar chave pública Kyber
        addSecurityLog('key_import', `Chave pública Kyber importada`, true)
        toast.success('Chave pública Kyber importada')
        return true
      } else if (type === 'dilithium' && decoded.dilithiumPublicKey) {
        // Validar e armazenar chave pública Dilithium
        addSecurityLog('key_import', `Chave pública Dilithium importada`, true)
        toast.success('Chave pública Dilithium importada')
        return true
      }
      
      return false
    } catch (error) {
      console.error('Erro ao importar chave:', error)
      addSecurityLog('key_import', `Falha na importação de chave ${type}`, false)
      toast.error('Erro ao importar chave pública')
      return false
    }
  }, [addSecurityLog])

  const validateKeyIntegrity = useCallback(async () => {
    if (!state.kyberKeys || !state.dilithiumKeys) {
      return false
    }

    try {
      // Teste básico de integridade das chaves
      const testData = 'test_integrity_check'
      const encrypted = await encryptData(testData, state.kyberKeys.publicKey)
      
      if (!encrypted) return false
      
      const decrypted = await decryptData(encrypted)
      const isValid = decrypted === testData
      
      addSecurityLog('key_validation', `Validação de integridade: ${isValid ? 'sucesso' : 'falha'}`, isValid)
      return isValid
    } catch (error) {
      console.error('Erro na validação de integridade:', error)
      addSecurityLog('key_validation', 'Erro na validação de integridade', false)
      return false
    }
  }, [state.kyberKeys, state.dilithiumKeys, encryptData, decryptData, addSecurityLog])

  const getKeyMetrics = useCallback(() => {
    if (!state.kyberKeys) {
      return { 
        strength: 0, 
        algorithm: 'none', 
        age: 0,
        totalKeys: 0,
        operationsToday: 0,
        lastRotation: null,
        algorithms: { kyber: 'none', dilithium: 'none' }
      }
    }

    const age = state.kyberKeys.createdAt ? 
      Math.floor((Date.now() - new Date(state.kyberKeys.createdAt).getTime()) / (1000 * 60 * 60 * 24)) : 0
    
    const strengthMap: Record<string, number> = {
      'Kyber-512': 70,
      'Kyber-768': 85,
      'Kyber-1024': 95
    }

    const totalKeys = (state.kyberKeys ? 1 : 0) + (state.dilithiumKeys ? 1 : 0)
    const operationsToday = state.securityLogs.filter(log => {
      const today = new Date().toDateString()
      return new Date(log.timestamp).toDateString() === today
    }).length

    const lastRotationLog = state.securityLogs
      .filter(log => log.action === 'key_rotation')
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())[0]

    return {
      strength: strengthMap[state.kyberKeys.algorithm] || 0,
      algorithm: state.kyberKeys.algorithm,
      age,
      totalKeys,
      operationsToday,
      lastRotation: lastRotationLog?.timestamp || null,
      algorithms: {
        kyber: state.kyberKeys?.algorithm || 'none',
        dilithium: state.dilithiumKeys?.algorithm || 'none'
      }
    }
  }, [state.kyberKeys, state.dilithiumKeys, state.securityLogs])

  // Monitorar mudanças no estado isInitialized
  useEffect(() => {
    console.log('🔄 MUDANÇA NO ESTADO isInitialized:', {
      isInitialized: state.isInitialized,
      hasKyberKeys: !!state.kyberKeys,
      hasDilithiumKeys: !!state.dilithiumKeys,
      timestamp: new Date().toISOString()
    })
  }, [state.isInitialized, state.kyberKeys, state.dilithiumKeys])

  // Atualizar securityStatus sempre que as chaves mudarem
  useEffect(() => {
    console.log('🔄 ATUALIZANDO SECURITY STATUS - Chaves mudaram:', {
      hasKyberKeys: !!state.kyberKeys,
      hasDilithiumKeys: !!state.dilithiumKeys,
      currentMessage: state.securityStatus.message
    })
    
    const newSecurityStatus = getSecurityStatusForKeys(state.kyberKeys, state.dilithiumKeys)
    
    // Só atualizar se o status realmente mudou
    if (newSecurityStatus.message !== state.securityStatus.message || 
        newSecurityStatus.level !== state.securityStatus.level) {
      console.log('📊 Atualizando securityStatus:', {
        from: { level: state.securityStatus.level, message: state.securityStatus.message },
        to: { level: newSecurityStatus.level, message: newSecurityStatus.message }
      })
      
      setState(prev => ({
        ...prev,
        securityStatus: {
          ...newSecurityStatus,
          lastCheck: new Date().toISOString()
        }
      }))
    }
  }, [state.kyberKeys, state.dilithiumKeys, getSecurityStatusForKeys])

  // Atualizar status de segurança periodicamente com throttling
  useEffect(() => {
    if (state.isInitialized) {
      console.log('⏰ Iniciando verificação periódica de segurança (throttled)')
      let lastUpdate = 0
      const THROTTLE_INTERVAL = 5 * 60 * 1000 // 5 minutos
      
      const interval = setInterval(() => {
        const now = Date.now()
        if (now - lastUpdate >= THROTTLE_INTERVAL) {
          console.log('🔄 [usePQC] Verificação periódica de segurança executada')
          const securityStatus = getSecurityStatus()
          setState(prev => ({
            ...prev,
            securityStatus: {
              ...securityStatus,
              lastCheck: new Date().toISOString()
            }
          }))
          lastUpdate = now
        }
      }, 60000) // Verificar a cada minuto, mas só atualizar a cada 5 minutos

      return () => {
        console.log('⏰ Parando verificação periódica de segurança')
        clearInterval(interval)
      }
    }
  }, [state.isInitialized, getSecurityStatus])

  // Registrar função de revalidação com o sistema de sincronização (throttled)
  useEffect(() => {
    if (!user) return;

    let lastRevalidation = 0;
    const REVALIDATION_THROTTLE = 30 * 1000; // 30 segundos

    const unregister = registerRevalidation(async () => {
      const now = Date.now();
      if (now - lastRevalidation < REVALIDATION_THROTTLE) {
        console.log('🔄 [usePQC] Revalidação ignorada (throttled)');
        return;
      }

      console.log('🔄 [usePQC] Revalidação automática após refresh...');
      lastRevalidation = now;
      
      try {
        // Verificar se já temos chaves válidas antes de recarregar
        if (state.isInitialized && state.kyberKeys && state.dilithiumKeys) {
          console.log('🔄 [usePQC] Chaves já inicializadas, pulando revalidação');
          return;
        }

        // Recarregar chaves do banco de dados
        await loadKeysFromDatabase();
        
        // Se não encontrou chaves no banco, tentar inicializar novamente
        const userKeys = pqcCrypto.getUserKeys(user.id);
        if (!userKeys.kyber || !userKeys.dilithium) {
          console.log('🔄 [usePQC] Chaves não encontradas, reinicializando sistema...');
          await initializePQC();
        }
        
        console.log('✅ [usePQC] Revalidação PQC concluída com sucesso');
      } catch (error) {
        console.error('❌ [usePQC] Erro na revalidação:', error);
      }
    });

    return unregister;
  }, [user, registerRevalidation, loadKeysFromDatabase, initializePQC, state.isInitialized, state.kyberKeys, state.dilithiumKeys]);

  return {
    ...state,
    generateKeys,
    encryptData,
    decryptData,
    signData,
    verifySignature,
    encryptAndSign,
    decryptAndVerify,
    saveKeysToDatabase,
    loadKeysFromDatabase,
    removeKeys,
    getPublicKeys,
    hasValidKeys,
    // Novas funcionalidades avançadas
    backupKeys,
    restoreKeys,
    rotateKeys,
    checkKeyExpiration,
    getSecurityStatus,
    addSecurityLog,
    clearSecurityLogs,
    exportPublicKeys,
    importPublicKey,
    validateKeyIntegrity,
    getKeyMetrics
  }
}

export default usePQC